import { useCallback } from "react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { StatCard } from "../components/common/StatCard";
import { DataTable } from "../components/common/DataTable";
import { FilterBar } from "../components/common/FilterBar";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { useFiltering } from "../hooks/useFiltering";
import { ideasAPI, schoolsAPI } from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { formatDate } from "../utils/formatters";
import { getSchoolName, handleSchoolId } from "../utils/helpers";
import { SchoolFilter } from "../components/filters/SchoolFilter";
import { Lightbulb, CheckCircle, XCircle } from "lucide-react";
import { Idea, ApiDataResponse } from "../types";

export function IdeasPage() {
  const {
    data: ideasData,
    isLoading,
    error,
  } = useDataFetching(QUERY_KEYS.IDEAS, {
    queryFn: ideasAPI.getAllIdeas,
  });

  const { data: schoolsData } = useDataFetching(QUERY_KEYS.SCHOOLS, {
    queryFn: schoolsAPI.getAllSchools,
  });

  const ideas = Array.isArray(ideasData) ? ideasData : (((ideasData as ApiDataResponse<Idea[]>)?.data || []) as Idea[]);
  const schools = Array.isArray(schoolsData) ? schoolsData : [];

  const {
    searchTerm,
    setSearchTerm,
    filters,
    updateFilter,
    filteredData: filteredIdeas,
  } = useFiltering({
    data: ideas,
    searchFields: ['title', 'description'],
    filterFn: (idea, filters) => {
      const schoolMatch = filters.schoolId
        ? handleSchoolId(idea.school_id) === filters.schoolId
        : true;
      return schoolMatch;
    },
  });

  const activeIdeas = filteredIdeas.filter(
    (idea: Idea) => idea.is_active
  ).length;
  const inactiveIdeas = filteredIdeas.filter(
    (idea: Idea) => !idea.is_active
  ).length;

  const handleSchoolChange = useCallback((schoolId: string) => {
    updateFilter('schoolId', schoolId);
  }, [updateFilter]);

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isActive
            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
        }`}
      >
        {isActive ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  const columns = [
    {
      key: 'title',
      header: 'Title',
      render: (idea: Idea) => (
        <span className="font-medium">{idea.title}</span>
      ),
    },
    {
      key: 'looking_for',
      header: 'Looking For',
      render: (idea: Idea) => (
        <span className="text-muted-foreground">{idea.looking_for}</span>
      ),
    },
    {
      key: 'school_id',
      header: 'School',
      render: (idea: Idea) => getSchoolName(handleSchoolId(idea.school_id), schools),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (idea: Idea) => getStatusBadge(idea.is_active),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (idea: Idea) => (
        <span className="text-muted-foreground">
          {formatDate(idea.createdAt)}
        </span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState type="table" />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <ErrorState
          title="Error loading ideas data"
          message="Please try refreshing the page or log in again"
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Ideas"
            description="Manage and view all ideas shared on the Walky platform"
          />

          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              title="Total Ideas"
              value={filteredIdeas.length}
              description="All submitted ideas"
              icon={<Lightbulb className="h-4 w-4" />}
              variant="blue"
              isLoading={isLoading}
            />

            <StatCard
              title="Active Ideas"
              value={activeIdeas}
              description="Currently active"
              icon={<CheckCircle className="h-4 w-4" />}
              variant="green"
              isLoading={isLoading}
            />

            <StatCard
              title="Inactive Ideas"
              value={inactiveIdeas}
              description="Not currently active"
              icon={<XCircle className="h-4 w-4" />}
              variant="amber"
              isLoading={isLoading}
            />
          </div>

          <DataTable
            data={filteredIdeas}
            columns={columns}
            title="All Ideas"
            headerActions={
              <FilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search ideas by title or description..."
                resultCount={{
                  filtered: filteredIdeas.length,
                  total: ideas.length,
                }}
              >
                <SchoolFilter
                  selectedSchoolId={filters.schoolId ? String(filters.schoolId) : ''}
                  onSchoolChange={handleSchoolChange}
                />
              </FilterBar>
            }
            emptyMessage="No ideas found"
            emptyDescription={
              searchTerm || filters.schoolId
                ? "Try adjusting your search filters"
                : "No ideas have been submitted yet"
            }
            isLoading={isLoading}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}               